/*
 * Copyright (c) 2025, Gather
 * https://gather.life
 */

'use client';

import { Grid2 as Grid, IconButton, type Theme } from '@mui/material';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import {
  useState,
  useEffect,
  type FunctionComponent,
  useContext,
  createContext,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Sheet, type SheetRef } from 'react-modal-sheet';

import type {
  BottomSheetData,
  BottomSheetProviderContextProps,
  BottomSheetProviderProps,
} from './bottom-sheet-provider.interface';
import { Icon, Typography } from '@/components';

const BottomSheetProviderContext = createContext<BottomSheetProviderContextProps>(
  {} as BottomSheetProviderContextProps
);

export const BottomSheetProvider: FunctionComponent<BottomSheetProviderProps> = ({
  children,
  className,
  pathname,
}) => {
  const sheetRef = useRef<SheetRef>(null);
  const snapTo = (i: number): Promise<void> | undefined => sheetRef.current?.snapTo(i);

  const [isOpen, setIsOpen] = useState(false);
  const [bottomSheetData, setBottomSheetData] = useState<BottomSheetData | null>(null);

  // hide the bottom sheet
  const hideBottomSheet = useCallback(() => {
    // Close
    setIsOpen(false);
  }, []);

  // after the bottom sheet is hidden
  const afterBottomSheetHidden = useCallback(() => {
    // Then clear data
    setBottomSheetData(null);
  }, []);

  // show the bottom sheet
  const showBottomSheet = useCallback((eventSheetData: BottomSheetData) => {
    // Set the data
    setBottomSheetData({
      // defaults
      // disableScrollMainContent: false, // need the undefined state
      // dismissible: true, // need the undefined state
      draggable: true,
      initialOpenPosition: 'content',
      initialSnapPoint: 0,
      modal: false,
      showCloseButton: true,
      // settings and overrides
      ...eventSheetData,
    });

    // Open the sheet
    setIsOpen(true);
  }, []);

  // hide dialog when navigating to different page
  useEffect(() => {
    hideBottomSheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]); // Only run when pathname changes, not when isOpen changes

  // create the bottom sheet context
  const bottomSheetContext = useMemo(
    () => ({
      isOpen,
      hideBottomSheet,
      showBottomSheet,
      snapTo,
    }),
    [isOpen, hideBottomSheet, showBottomSheet]
  );

  const disableScrollLocking = useMemo(() => {
    /*
     * The plugin's scroll lock is ON by default for all modes
     * It makes more sense to me to default to NOT locking content for non-modal bottom sheet, selectively choose when to lock the content behind the sheet.
     * Only a "modal" type bottom sheet will lock content by default
     */

    const isModal = bottomSheetData?.modal ?? false;
    const disableScroll = bottomSheetData?.disableScrollMainContent;

    // If isModal is TRUE AND disableScroll is TRUE OR UNDEFINED
    if (isModal && disableScroll !== false) {
      return false;
    }

    // If disableScroll is FALSE, if UNDEFINED treat disableScroll as FALSE
    return !(disableScroll ?? false);
  }, [bottomSheetData?.disableScrollMainContent, bottomSheetData?.modal]);
  const isDismissible = useMemo(() => {
    /*
     * The plugin ENABLEs "dismissible" by default for all modes
     * It makes more sense to me to default to keep it that way for non-modal bottom sheet.
     * Only a "modal" type bottom sheet will DISABLE "dismissible" by default
     */

    const isModal = bottomSheetData?.modal ?? false;
    const innerIsDismissible = bottomSheetData?.dismissible;

    // If isModal is TRUE AND dismissible is FALSE
    if (isModal && innerIsDismissible === false) {
      return false;
    }

    // If innerIsDismissible is FALSE, if UNDEFINED treat innerIsDismissible as FALSE
    return !(innerIsDismissible ?? false);
  }, [bottomSheetData?.dismissible, bottomSheetData?.modal]);
  const dragIndicatorSx = useMemo(() => {
    return { '& .react-modal-sheet-drag-indicator': { width: '28px !important' } };
  }, []);
  const closeButtonPadding = useMemo(() => {
    if (bottomSheetData?.draggable ?? false) {
      return (theme: Theme) => theme.spacing(1, 4, 4, 4);
    }

    return 4;
  }, [bottomSheetData?.draggable]);
  const contentPadding = useMemo(() => {
    if (bottomSheetData?.showCloseButton ?? false) {
      return (theme: Theme) => theme.spacing(0, 5, 5, 5);
    }

    return 5;
  }, [bottomSheetData?.showCloseButton]);

  return (
    <BottomSheetProviderContext.Provider value={bottomSheetContext}>
      {children}

      {bottomSheetData && isOpen && (
        <Sheet
          className={classNames('bottom-sheet-provider', className)}
          detent={bottomSheetData.initialOpenPosition}
          disableDismiss={!isDismissible}
          disableDrag={!bottomSheetData.draggable}
          disableScrollLocking={disableScrollLocking}
          isOpen={isOpen}
          // modalEffectRootId='root' // have to explore the iOS modal effect
          snapPoints={bottomSheetData.snapPoints}
          onClose={() => hideBottomSheet()}
          onCloseEnd={() => afterBottomSheetHidden()}
        >
          <Sheet.Container>
            <Sheet.Content>
              {bottomSheetData.draggable && (
                <Grid container justifyContent='center' paddingTop={3} sx={dragIndicatorSx}>
                  <Sheet.DragIndicator />
                </Grid>
              )}
              {bottomSheetData.showCloseButton && (
                <Grid container justifyContent='end' padding={closeButtonPadding}>
                  <IconButton
                    aria-label='close'
                    color='secondary'
                    size='small'
                    onClick={hideBottomSheet}
                  >
                    <Icon icon='close' />
                  </IconButton>
                </Grid>
              )}
              <Grid container padding={contentPadding} spacing={5}>
                {!isEmpty(bottomSheetData.title) && (
                  <Sheet.Header>
                    {typeof bottomSheetData.title === 'string' ? (
                      <Typography size='small' variant='headline'>
                        {bottomSheetData.title}
                      </Typography>
                    ) : (
                      bottomSheetData.title
                    )}
                  </Sheet.Header>
                )}
                {bottomSheetData.contents}
                {bottomSheetData.actions}
              </Grid>
            </Sheet.Content>
          </Sheet.Container>
          {bottomSheetData.modal && (
            <Sheet.Backdrop onTap={bottomSheetData.dismissible ? hideBottomSheet : undefined} />
          )}
        </Sheet>
      )}
    </BottomSheetProviderContext.Provider>
  );
};

export const useBottomSheetProvider = (): BottomSheetProviderContextProps => {
  return useContext(BottomSheetProviderContext);
};
